import { useEffect, useRef, type ReactNode } from 'react';
import type { Icon as IconSpec } from '../commands/types';
import { closeModal } from '../state/store';

export function Icon({ icon, size = 22 }: { icon: IconSpec; size?: number }) {
  if ('g' in icon) {
    return (
      <span className="icon icon-glyph" aria-hidden="true" style={{ fontSize: icon.size ?? size, transform: icon.dy ? `translateY(${icon.dy}px)` : undefined }}>
        {icon.g}
      </span>
    );
  }
  if ('svg' in icon) {
    return (
      <svg className="icon icon-svg" width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" fill={icon.fill ? 'currentColor' : 'none'} stroke={icon.fill ? 'none' : 'currentColor'} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
        <path d={icon.svg} />
      </svg>
    );
  }
  return (
    <span className={`icon icon-text${icon.italic ? ' italic' : ''}${icon.bold ? ' bold' : ''}${icon.serif ? ' serif' : ''}`} aria-hidden="true">
      {icon.t}
    </span>
  );
}

/** Accessible modal dialog: focus trap-lite, Escape closes, focus returns. */
export function Modal({ title, children, footer, onClose = closeModal, wide, className }: { title: string; children: ReactNode; footer?: ReactNode; onClose?: () => void; wide?: boolean; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    const first = el?.querySelector<HTMLElement>('[autofocus], input, select, textarea, button:not(.modal-close)');
    (first ?? el)?.focus();
    return () => prev?.focus?.();
  }, []);
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`modal${wide ? ' wide' : ''}${className ? ' ' + className : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={ref}
        tabIndex={-1}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            onClose();
          }
          if (e.key === 'Tab' && ref.current) {
            const f = [...ref.current.querySelectorAll<HTMLElement>('button, input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter((x) => !x.hasAttribute('disabled'));
            if (!f.length) return;
            const i = f.indexOf(document.activeElement as HTMLElement);
            if (e.shiftKey && i <= 0) {
              e.preventDefault();
              f[f.length - 1].focus();
            } else if (!e.shiftKey && i === f.length - 1) {
              e.preventDefault();
              f[0].focus();
            }
          }
        }}
      >
        <header className="modal-head">
          <h2>{title}</h2>
          <button className="modal-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-foot">{footer}</footer>}
      </div>
    </div>
  );
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

export function Segmented<T extends string | number>({ value, options, onChange, label }: { value: T; options: { value: T; label: ReactNode }[]; onChange: (v: T) => void; label: string }) {
  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button key={String(o.value)} role="radio" aria-checked={o.value === value} className={o.value === value ? 'on' : ''} onClick={() => onChange(o.value)} type="button">
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label className="toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="toggle-track" aria-hidden="true">
        <span className="toggle-thumb" />
      </span>
      <span>{label}</span>
    </label>
  );
}
